import type { HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpContext, HttpContextToken } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

/** Optional owner policy for the lifetime of a mounted dialog. */
export interface DialogLifecycleHandle {
    readonly element: HTMLElement | null;
    readonly closed$: Observable<unknown>;
    close(): void;
    hasUnsavedChanges(): boolean;
}
export const DIALOG_LIFECYCLE = new InjectionToken<
    (dialog: DialogLifecycleHandle) => void
>('DIALOG_LIFECYCLE');

/** Unsaved changes explicitly accepted for this navigation only. */
export const UNSAVED_CHANGES_ACCEPTED = Symbol('UNSAVED_CHANGES_ACCEPTED');

/**
 * Set this context to true on an HttpClient request to suppress automatic error toasts.
 * Note: 401 errors will always display a toast and trigger logout regardless of this flag.
 */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** Offer immediate sign-in recovery on the existing mandatory 401 toast. */
export const UNAUTHORISED_TOAST_ACTION = new HttpContextToken<boolean>(
    () => false
);

/** Permission probes must not recursively recover their own failures. */
export const BUILDING_PERMISSION_PROBE = new HttpContextToken<boolean>(
    () => false
);

/** Building identity for Core operations whose URL does not contain it. */
export const REQUEST_BUILDING_ID = new HttpContextToken<string | null>(
    () => null
);

/**
 * Generic helper: creates an HttpContext with multiple token-value pairs.
 * Returns undefined if no tokens are provided and no existing context exists.
 *
 * @example
 * ```ts
 * // Single token
 * const context = createHttpContext([
 *   [SUPPRESS_ERROR_TOAST, true]
 * ]);
 *
 * // Multiple tokens
 * const context = createHttpContext([
 *   [SUPPRESS_ERROR_TOAST, true],
 *   [SOME_OTHER_TOKEN, someValue]
 * ], existingContext);
 * ```
 */
export function createHttpContext(
    entries: Array<[HttpContextToken<unknown>, unknown]>,
    existing?: HttpContext
): HttpContext | undefined {
    if (entries.length === 0) return existing;

    const context = existing ?? new HttpContext();
    for (const [token, value] of entries) {
        context.set(token, value);
    }
    return context;
}

// Central options type for @services methods
export interface ApiRequestOptions {
    suppressAutoErrorToast?: boolean;
    unauthorisedToastAction?: boolean;
}

/**
 * Convenience helper: creates an HttpContext from ApiRequestOptions and additional token entries.
 * Automatically handles suppressAutoErrorToast option and merges with additional entries.
 *
 * @example
 * ```ts
 * // With options only
 * const context = createHttpContextFromOptions({ suppressAutoErrorToast: true });
 *
 * // With options and additional tokens
 * const context = createHttpContextFromOptions(
 *   { suppressAutoErrorToast: true },
 *   [[SOME_OTHER_TOKEN, someValue]]
 * );
 *
 * // With existing context
 * const context = createHttpContextFromOptions(
 *   { suppressAutoErrorToast: true },
 *   [[SOME_OTHER_TOKEN, someValue]],
 *   existingContext
 * );
 * ```
 */
export function createHttpContextFromOptions(
    options?: ApiRequestOptions,
    additionalEntries?: Array<[HttpContextToken<unknown>, unknown]>,
    existing?: HttpContext
): HttpContext | undefined {
    const entries: Array<[HttpContextToken<unknown>, unknown]> = [];

    if (options?.suppressAutoErrorToast) {
        entries.push([SUPPRESS_ERROR_TOAST, true]);
    }

    if (options?.unauthorisedToastAction) {
        entries.push([UNAUTHORISED_TOAST_ACTION, true]);
    }

    if (additionalEntries) {
        entries.push(...additionalEntries);
    }

    return createHttpContext(entries, existing);
}

export interface HttpClientOptions {
    headers?: HttpHeaders | Record<string, string | string[]>;
    context?: HttpContext;
    params?:
        | HttpParams
        | Record<
              string,
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>
          >;
    reportProgress?: boolean;
    withCredentials?: boolean;
    credentials?: RequestCredentials;
    keepalive?: boolean;
    priority?: RequestPriority;
    cache?: RequestCache;
    mode?: RequestMode;
    redirect?: RequestRedirect;
    referrer?: string;
    integrity?: string;
    transferCache?: { includeHeaders?: string[] } | boolean;
    timeout?: number;
}
